import {
  type Dir,
  initialRun,
  LEVELS,
  levelById,
  levelNumber,
  nextLevel,
  type Pos,
  type RunState,
  stars as starsFor,
  step,
} from '@xox/shared'
import { ArrowLeft, RotateCcw } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { BoardOverlay } from '../components/BoardOverlay'
import { DPad } from '../components/DPad'
import { Button, Card, LinkButton, Stars } from '../components/ui'
import { useDirectionInput } from '../hooks/useDirectionInput'
import { feedback } from '../lib/feedback'
import { plural } from '../lib/format'
import { useSession } from '../store/session'
import { FALL_MS, glideMs } from './anim'
import { IceBoard } from './IceBoard'
import { isUnlocked, loadProgress, recordResult, saveProgress } from './progress'

type Status = 'playing' | 'won'

export function SoloGame() {
  const { levelId = '' } = useParams()
  const level = levelById(levelId)
  const navigate = useNavigate()
  const me = useSession((s) => s.me)

  const [run, setRun] = useState<RunState>(() => (level ? initialRun(level) : NOWHERE))
  const [shown, setShown] = useState<Pos>(run.pos)
  const [falls, setFalls] = useState(0)
  const [status, setStatus] = useState<Status>('playing')
  const [falling, setFalling] = useState(false)
  const [announce, setAnnounce] = useState('')
  const busyUntil = useRef(0)

  const reset = useCallback(() => {
    if (!level) return
    const r = initialRun(level)
    setRun(r)
    setShown(r.pos)
    setFalls(0)
    setStatus('playing')
    setFalling(false)
    setAnnounce('Back to the start')
  }, [level])

  useEffect(() => {
    reset()
  }, [reset])

  const move = useCallback(
    (dir: Dir) => {
      if (!level || status !== 'playing') return
      if (Date.now() < busyUntil.current) return
      const r = step(level, run, dir)
      if (r.path.length === 0) {
        feedback('thud')
        if (r.outcome === 'locked') setAnnounce('The door is locked — find the key first')
        return
      }
      const glide = glideMs(r.path.length)
      feedback('slide')
      if (r.gotKey) setTimeout(() => feedback('key'), glide / 2)
      setShown(r.path[r.path.length - 1]!)
      if (r.outcome === 'fell' || r.outcome === 'caught') {
        busyUntil.current = Date.now() + glide + FALL_MS
        setTimeout(() => {
          setFalling(true)
          feedback(r.outcome === 'caught' ? 'caught' : 'splash')
          setFalls((f) => f + 1)
          setAnnounce(
            r.outcome === 'caught'
              ? 'A yeti got you — back to the start'
              : 'Fell through the ice — back to the start',
          )
          setTimeout(() => {
            setFalling(false)
            setRun(r.run)
            setShown(r.run.pos)
          }, FALL_MS)
        }, glide)
        return
      }
      busyUntil.current = Date.now() + glide
      setTimeout(() => setRun(r.run), glide)
      if (r.outcome === 'door') {
        setTimeout(() => {
          feedback('door')
          setStatus('won')
          setAnnounce(`Reached the door in ${plural(r.run.moves, 'move')}`)
          saveProgress(recordResult(loadProgress(), level.id, r.run.moves, level.par))
        }, glide)
      } else {
        setAnnounce(
          `Slid ${plural(r.path.length, 'cell')}${r.gotKey ? ' and grabbed the key' : ''}`,
        )
      }
    },
    [level, run, status],
  )

  const swipe = useDirectionInput(move, status === 'playing')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'r' || e.key === 'R') reset()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [reset])

  if (!level || !isUnlocked(loadProgress(), LEVELS, level.id)) {
    return (
      <Card className="mx-auto max-w-md text-center">
        <h1 className="text-xl font-bold">{level ? 'Locked' : 'Unknown level'}</h1>
        {level && <p className="mt-1 text-slate-500">Clear the previous level first.</p>}
        <Button className="mt-4" onClick={() => navigate('/ice')}>
          Back to levels
        </Button>
      </Card>
    )
  }

  const next = nextLevel(level.id)
  const earned = status === 'won' ? starsFor(run.moves, level.par) : 0
  const n = levelNumber(level.id)

  return (
    <div className="flex flex-1 flex-col items-center gap-4">
      <div className="flex w-full max-w-[400px] items-center justify-between gap-3">
        <LinkButton to="/ice" className="px-2">
          <ArrowLeft className="size-4" aria-hidden /> Levels
        </LinkButton>
        <div className="text-center">
          <div className="font-bold">
            <span className="text-slate-400">#{n}</span> {level.name}
          </div>
          <div className="text-xs text-slate-500">
            par {level.par} · {plural(run.moves, 'move')}
            {falls > 0 && ` · ${plural(falls, 'fall')}`}
            {run.hasKey && ' · 🔑'}
          </div>
        </div>
        <Button variant="ghost" className="px-2" onClick={reset} aria-label="Restart level (R)">
          <RotateCcw className="size-4" aria-hidden />
        </Button>
      </div>

      <div className="relative" {...swipe}>
        <IceBoard
          level={level}
          run={run}
          doorReached={status === 'won'}
          pucks={[
            {
              id: me?.playerId ?? 'solo',
              pos: shown,
              color: me?.color,
              falling,
              finished: status === 'won',
            },
          ]}
        />
        {status === 'won' && (
          <BoardOverlay>
            <Stars earned={earned} size="lg" />
            <div className="mt-2 text-2xl font-black">Level clear!</div>
            <div className="text-sm text-slate-500">
              {plural(run.moves, 'move')} · par {level.par}
              {falls > 0 && ` · ${plural(falls, 'fall')}`}
            </div>
            <div className="mt-4 flex flex-col gap-2">
              {next ? (
                <Button onClick={() => navigate(`/ice/play/${next.id}`)}>
                  Next level: {next.name}
                </Button>
              ) : (
                <Button onClick={() => navigate('/ice')}>All levels cleared — back</Button>
              )}
              <Button variant="secondary" onClick={reset}>
                <RotateCcw className="size-4" aria-hidden /> Retry
              </Button>
            </div>
          </BoardOverlay>
        )}
      </div>

      <p className="text-sm text-slate-500" aria-live="polite">
        {announce || 'Arrow keys, WASD, swipe, or the arrows below.'}
      </p>
      <div className="sm:hidden">
        <DPad onMove={move} disabled={status !== 'playing'} />
      </div>
    </div>
  )
}

const NOWHERE: RunState = { pos: { r: 0, c: 0 }, moves: 0, cracked: [], hasKey: false }
